import type { NamedEmail } from '~/util/namedEmailFormatter';
import type { Reducer } from 'react';
import type { Descendant } from 'slate';
import type { EmailItemType } from '~/components/Inputs/TagListInputs/EmailSelectInput';
import type { OptionOf } from '~/components/Dropdown';
import { assertNever } from '~/util/assertion';

export type ReducerAction<T, P extends object> = {
  type: T;
  payload: P;
};

export type Action =
  | ReducerAction<'setKey', { key: string; value: string | number }>
  | ReducerAction<'addToList', { key: string; value: EmailItemType }>
  | ReducerAction<
      'removeFromList',
      { key: string; removedValue: EmailItemType }
    >
  | ReducerAction<'reset', { value: EmailReducerState }>;

export type EmailReducerState = {
  subject: string;
  body: Array<Descendant>;
  senderOptions: Array<OptionOf<NamedEmail>>;
  selectedSenderIdx: number | null;
  senderValue?: NamedEmail | null;
  selectedCCs: Array<EmailItemType>;
  selectedBCCs: Array<EmailItemType>;
};

export type ReducerType = Reducer<EmailReducerState, Action>;

const sendEmail = (state: EmailReducerState, action: Action) => {
  switch (action.type) {
    case 'reset':
      return action.payload.value;

    case 'setKey': {
      const { key, value } = action.payload;

      return {
        ...state,
        [key]: value,
      };
    }

    case 'addToList': {
      const { key, value } = action.payload;

      return {
        ...state,
        [key]: [...state[key], value],
      };
    }

    case 'removeFromList': {
      const { key, removedValue } = action.payload;

      return {
        ...state,
        [key]: state[key].filter(
          selectedValue =>
            selectedValue.namedEmail.email !== removedValue.namedEmail.email,
        ),
      };
    }

    default: {
      return assertNever(action, 'sendEmailReducer');
    }
  }
};

export default sendEmail;
